import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../utils/api";
import { UserPlus } from "lucide-react";
import EmployeeModal from "./EmployeeModal";
import {
  Card,
  Button,
  PageHeader,
  AdminLayout,
  Badge,
  LoadingSpinner,
  EmptyState,
  useToast,
} from "../components/AdminSharedComponents";

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const response = await api.get("/api/employees");
        setEmployees(response.data.data || []);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setEmployees([]);
        toast.error("Gagal memuat data karyawan");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Manage scroll lock when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  const openModal = (employee) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setIsModalOpen(false);
  };

  const handleAddEmployee = () => {
    navigate("/admin/employees/add");
  };

  const handleEditEmployee = (employee) => {
    navigate(`/admin/employees/edit/${employee.id}`);
    closeModal();
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      const response = await api.delete(`/api/employees/${employeeId}`);
      if (response.status === 204) {
        setEmployees(employees.filter((emp) => emp.id !== employeeId));
        closeModal();
        toast.success("Karyawan berhasil dihapus");
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Gagal menghapus karyawan. Silakan coba lagi.");
    }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Employees"
        subtitle="Manage employee records and details"
        actions={
          <Button variant="primary" onClick={handleAddEmployee}>
            <div className="flex items-center">
              <UserPlus size={18} className="mr-2" />
              Add Employee
            </div>
          </Button>
        }
      />

      <Card title="Employee Directory" accentColor="dark">
        {loading ? (
          <LoadingSpinner message="Memuat data karyawan..." />
        ) : employees.length > 0 ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {employees.map((employee) => {
              // tentukan sumber data
              const displayName = employee.user
                ? employee.user.name
                : employee.name;
              const displayEmail = employee.user
                ? employee.user.email
                : employee.email;
              const displayRole = employee.user
                ? employee.user.role
                : "non-login";
              const displayStatus = employee.user ? "Aktif" : "Non-login";

              return (
                <div
                  key={employee.id}
                  onClick={() => openModal(employee)}
                  className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-100 cursor-pointer transition-all duration-200 hover:scale-102 hover:shadow-lg hover:border-gray-200"
                >
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center overflow-hidden">
                    {employee.photo ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${employee.photo}`}
                        alt={displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='none' stroke='%234b80b6' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z'/%3E%3C/svg%3E";
                        }}
                      />
                    ) : (
                      <div className="rounded-full bg-gray-200 w-16 h-16 flex items-center justify-center text-primary">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-8 h-8"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-1 truncate text-primary-dark">
                      {displayName || "Tanpa Nama"}
                    </h3>
                    <p className="truncate text-primary">
                      {displayEmail || "-"}
                    </p>
                    <p className="text-sm text-gray-600 mb-1 capitalize">
                      {displayRole}
                    </p>
                    <Badge variant={employee.user ? "primary" : "secondary"}>
                      {displayStatus}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            message="Belum ada karyawan. Klik 'Tambah Karyawan' untuk membuat data karyawan."
            action={{
              label: "Tambah Karyawan",
              onClick: handleAddEmployee,
            }}
          />
        )}
      </Card>

      {isModalOpen && selectedEmployee && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={closeModal}
          onEdit={handleEditEmployee}
          onDelete={handleDeleteEmployee}
        />
      )}
    </AdminLayout>
  );
};

export default Employees;
